/**
 * Formats francais exclusivement (CDC 9.2) : dates en JJ/MM/AAAA, montants
 * avec espace insecable et symbole euro. Le fuseau d'affichage est
 * Europe/Paris, la base stockant en UTC (CDC 10.4).
 */

const FUSEAU = 'Europe/Paris';

const formateurDate = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: FUSEAU,
});

const formateurDateHeure = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: FUSEAU,
});

const formateurMontant = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
});

export function formaterDate(valeur: string | null | undefined): string {
  if (!valeur) return '—';
  // Une date seule (AAAA-MM-JJ) n'a pas de fuseau : la lire en UTC evite
  // qu'elle recule d'un jour a l'affichage.
  const date = valeur.length === 10 ? new Date(`${valeur}T12:00:00Z`) : new Date(valeur);
  return Number.isNaN(date.getTime()) ? '—' : formateurDate.format(date);
}

export function formaterDateHeure(valeur: string | null | undefined): string {
  if (!valeur) return '—';
  const date = new Date(valeur);
  return Number.isNaN(date.getTime()) ? '—' : formateurDateHeure.format(date);
}

/**
 * Un mois en toutes lettres, depuis « 2025-09 » ou « 2025-09-01 ».
 *
 * Il vivait en cinq exemplaires dans les ecrans, dont deux qui lisaient
 * la date en heure locale et un en UTC : le 1er du mois y basculait au
 * mois precedent selon le fuseau. La lecture se fait donc ici, a midi
 * UTC, comme pour `formaterDate`.
 */
export function formaterMois(
  valeur: string | null | undefined,
  options: { court?: boolean; avecAnnee?: boolean } = {},
): string {
  if (!valeur) return '—';
  const jour = valeur.length === 7 ? `${valeur}-01` : valeur.slice(0, 10);
  const date = new Date(`${jour}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('fr-FR', {
    month: options.court ? 'short' : 'long',
    ...(options.avecAnnee === false ? {} : { year: 'numeric' }),
    timeZone: 'UTC',
  }).format(date);
}

/**
 * Saisie « JJ/MM/AAAA » → « AAAA-MM-JJ », `null` si elle n'est pas une
 * date. L'ISO est tolere : il arrive qu'on colle une date recopiee.
 *
 * Le calendrier natif d'un `input type="date"` reste la bonne surface de
 * saisie ; ceci sert la ou il n'y en a pas.
 */
export function depuisDateFr(saisie: string): string | null {
  const propre = saisie.trim();
  const fr = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(propre);
  const iso = fr ? `${fr[3]}-${fr[2]}-${fr[1]}` : propre;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  // Le format ne suffit pas : le 31/02 le respecte. On verifie que la
  // date existe en la relisant.
  const date = new Date(`${iso}T12:00:00Z`);
  return Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== iso ? null : iso;
}

/** « septembre 2025 » → « Septembre 2025 », pour un titre. */
export function capitaliser(texte: string): string {
  return texte.replace(/^./, (premier) => premier.toUpperCase());
}

/** Les montants circulent en CENTIMES entiers, jamais en flottant. */
export function formaterMontant(centimes: number | null | undefined): string {
  if (centimes === null || centimes === undefined) return '—';
  return formateurMontant.format(centimes / 100);
}

/** Saisie utilisateur en euros → centimes entiers. */
export function enCentimes(saisie: string): number | null {
  const nettoye = saisie.replace(/\s/g, '').replace(',', '.');
  if (!/^\d+(\.\d{0,2})?$/.test(nettoye)) return null;
  return Math.round(Number.parseFloat(nettoye) * 100);
}

export function enEuros(centimes: number | null | undefined): string {
  if (centimes === null || centimes === undefined) return '';
  return (centimes / 100).toFixed(2).replace('.', ',');
}

export function nomComplet(personne: { nom: string; prenom: string }): string {
  return `${personne.nom.toUpperCase()} ${personne.prenom}`;
}

/** Numerotation ISO : 1 = lundi. */
export const JOURS = [
  '',
  'Lundi',
  'Mardi',
  'Mercredi',
  'Jeudi',
  'Vendredi',
  'Samedi',
  'Dimanche',
] as const;

export function libelleJour(jour: number | null | undefined): string {
  return jour && jour >= 1 && jour <= 7 ? JOURS[jour]! : '—';
}

/** « 16:30:00 » → « 16h30 ». Les horaires du planning se lisent a la francaise. */
export function formaterHeure(valeur: string | null | undefined): string {
  if (!valeur) return '—';
  const [heures, minutes] = valeur.split(':');
  return `${heures}h${minutes ?? '00'}`;
}

export function formaterPlage(
  debut: string | null | undefined,
  fin: string | null | undefined,
): string {
  if (!debut || !fin) return '—';
  return `${formaterHeure(debut)}–${formaterHeure(fin)}`;
}

export function formaterDuree(minutes: number | null | undefined): string {
  if (!minutes) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m}`;
}

const LIBELLES_STATUT: Record<string, string> = {
  actif: 'Actif',
  inactif: 'Inactif',
  fin_anticipee: 'Fin anticipée',
};

export function libelleStatut(statut: string): string {
  return LIBELLES_STATUT[statut] ?? statut;
}

const LIBELLES_ACTIONS: Record<string, string> = {
  creation: 'Creation',
  modification: 'Modification',
  suppression: 'Suppression',
  consultation: 'Consultation',
  connexion: 'Connexion',
  deconnexion: 'Deconnexion',
  export: 'Export',
  elevation: 'Elevation',
  echec_connexion: 'Echec de connexion',
};

export function libelleAction(action: string): string {
  return LIBELLES_ACTIONS[action] ?? action;
}
